import cx from 'classnames';
import { gql } from "@apollo/client";
import { OrderRowFragment } from "../../codegen/graphql";
import * as countryCodes from 'country-codes-list';
import { getPlatformColor } from "../utils/getPlatformColor";
import { useMemo } from 'react';
import FulfillmentStatus from './common/FulfillmentStatus';
import { getTranslatedValue, Translations } from './common/TranslatedValue';


export const ORDER_ROW_FRAGMENT = gql`
  fragment OrderRow on Order {
    id
    orderId
    shopId
    shop {
      title
      shopType
    }
    orderDate
    orderStatus
    customerAddressCountry
    customerAddressCountryCode
    customerName
    createdAt
    updatedAt

    tasks {
      id
      completed
    }

    orderProductsVariations {
      id
      quantity

      adVariation {
        id
        ad {
          id
          title
        }
        variationTypeValues {
          id
          value
          type {
            type
          }
          medias {
            id
            url
          }
        }
      }
    }
  }
`;


export function OrderRow({
  order,
  handleRowClick,
}: {
  order: OrderRowFragment;
  handleRowClick: (orderId: string) => void;
}) {

  const activeTasks = order.tasks.reduce((acc, task) => acc + (task.completed ? 0 : 1), 0)
  const translationLang = order.customerAddressCountryCode;

  const externalOrderLink = useMemo(() => {
    if (order.shop.shopType === 'ebay') {
      return `https://www.ebay.fr/mesh/ord/details?orderid=${order.orderId}`;
    }
    if (order.shop.shopType === 'leboncoin') {
      return '';
    }
  }, [order.shop.shopType, order.orderId]);

  return (
    <a
      href={`/orders/${order.id}`}
      className="grid grid-cols-12 hover:bg-gray-50 transition-colors cursor-pointer"
      onClick={(e) => {
        if (e.ctrlKey) {
          return;
        }
        else {
          e.preventDefault();
          handleRowClick(order.id);
        }

      }}
    >
      <div className="col-span-2 px-3 py-2 border-r border-gray-200">
        <div className="flex flex-col">
          <div className="text-md font-medium text-blue-700 underline">
            <a href={externalOrderLink} target="_blank" rel="noopener noreferrer">
              #{order.orderId}
            </a>
          </div>
          <div
            className="text-md font-medium"
          >
            <a
              href={externalOrderLink}
              target="_blank"
              rel="noopener noreferrer"
              className={cx(
                "underline font-bold break-all",
                getPlatformColor(order.shop.shopType).textClass,
              )}
            >
              {order.shop.shopType}<br/>
              {order.shop.title}
            </a>
          </div>
          <div className="text-md text-gray-900">
            {formatDate(order.orderDate)}
          </div>
        </div>
      </div>
      <div className="col-span-2 px-3 py-2 border-r border-gray-200">
        <div className="text-md text-gray-900">
          {/* order.username && (
            <a
              href={`https://www.ebay.fr/usr/${order.username}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 underline"
          >
              {order.username || 'N/A'}
            </a>
          )*/}
          {countryCodes.findOne('countryCode', order.customerAddressCountryCode)?.flag}{' '}
          {order.customerAddressCountry}<br/>
          {order.customerName}
        </div>
      </div>
      <div className="col-span-2 px-3 py-2 border-r border-gray-200">
        <div className="p-1 inline-block rounded-md text-md text-gray-900">
          <FulfillmentStatus status={order.orderStatus} />
        </div>
        {activeTasks > 0 && (
          <div className="text-md text-orange-600 mt-1">
            {activeTasks} active tasks
          </div>
        )}
      </div>
      <div className="col-span-5 px-3 py-2 border-r border-gray-200">
        {order.orderProductsVariations.map((orderProductsVariation, index) => {
          const adTitle = getTranslatedValue(
            orderProductsVariation.adVariation?.ad?.title as unknown as Translations,
            translationLang ?? 'FR',
          );

          return (
            <div key={index} className="flex items-center space-x-2 py-1">
              {orderProductsVariation.adVariation?.variationTypeValues?.flatMap(typeValues => {
                return typeValues.medias.map(media => {
                  const images = [];
                  for (let i = 0; i < Math.min(orderProductsVariation.quantity, 3); i++) {
                    images.push(
                      <img
                        key={`${media.url}-${i}`}
                        src={media.url}
                        alt={adTitle}
                        className={`w-10 h-10 rounded-md ${i > 0 ? '-ml-2' : ''}`}
                        style={{ zIndex: 3 - i }}
                      />
                    );
                  }
                  return images;
                });
              })}
              <span className="shrink-0 bg-gray-200 text-gray-700 text-md font-semibold px-2 py-1 rounded-full">
                x {orderProductsVariation.quantity}
              </span>
              <span className="text-gray-900">
                {adTitle}
              </span>
            </div>
          );
        })}
      </div>
      <div className="col-span-1 px-3 py-2">
        <div className="text-md font-medium text-gray-900">
          TODO
        </div>
      </div>
    </a>
  );
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('fr-FR', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
};